import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from "@mui/material";
import { useState } from "react";
import ConfigUtils from "../../../utils/Config";
import IEntidadFacturacion from "../../../types/EntidadFacturacion";

// Sentinel interno: MUI Select no admite `null` como value, así que la entidad principal
// (entidadId: null) se representa aquí y se traduce de vuelta a null en onConfirm.
const PRINCIPAL_SENTINEL = "__principal__";

interface ICambiarEntidadDialogProps {
  open: boolean;
  onClose: () => void;
  entidadActualId: string | null;
  loading: boolean;
  onConfirm: (nuevaEntidadId: string | null) => void;
  // Un borrador cambia de entidad en el sitio (no se anula nada); una emitida se anula y se crea otra.
  esBorrador?: boolean;
}

// Diálogo de "cambiar entidad de facturación", reutilizado desde el detalle de factura y desde
// el detalle de bono. Solo entidades activas, sin incluir la actual — una entidad desactivada
// sigue siendo origen válido pero nunca destino.
const CambiarEntidadDialog: React.FC<ICambiarEntidadDialogProps> = ({ open, onClose, entidadActualId, loading, onConfirm, esBorrador = false }) => {
  const [selected, setSelected] = useState<string | null | undefined>(undefined);
  const [confirmarCambio, setConfirmarCambio] = useState(false);

  const entidadesDestino: { entidadId: string | null; nombre: string }[] = [
    { entidadId: null, nombre: ConfigUtils.getDatosFacturacion()?.nombre || "Principal" },
    ...ConfigUtils.getDatosFacturacionAdicional()
      .filter((e: IEntidadFacturacion) => e.active)
      .map((e: IEntidadFacturacion) => ({ entidadId: e._id, nombre: e.nombre })),
  ].filter(e => e.entidadId !== entidadActualId);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Cambiar entidad de facturación</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="cambiar-entidad-select-label">Nueva entidad</InputLabel>
          <Select
            labelId="cambiar-entidad-select-label"
            label="Nueva entidad"
            disabled={loading}
            value={selected === undefined ? "" : (selected === null ? PRINCIPAL_SENTINEL : selected)}
            onChange={e => {
              setSelected(e.target.value === PRINCIPAL_SENTINEL ? null : e.target.value);
              setConfirmarCambio(false);
            }}
          >
            {entidadesDestino.map(ent => (
              <MenuItem key={ent.entidadId ?? PRINCIPAL_SENTINEL} value={ent.entidadId ?? PRINCIPAL_SENTINEL}>
                {ent.nombre}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selected !== undefined && (
          <FormControlLabel
            sx={{ mt: 1, alignItems: "flex-start", "& .MuiFormControlLabel-label": { fontSize: "0.8rem", mt: "9px" } }}
            control={
              <Checkbox
                checked={confirmarCambio}
                disabled={loading}
                onChange={e => setConfirmarCambio(e.target.checked)}
              />
            }
            label={esBorrador
              ? "Entiendo que el borrador pasará a la entidad seleccionada"
              : "Entiendo que se anulará la factura actual y se creará otra con la nueva entidad"}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={loading || selected === undefined || !confirmarCambio}
          onClick={() => onConfirm(selected as string | null)}
        >
          {loading ? "Cambiando..." : "Confirmar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CambiarEntidadDialog;
